import type { ReactNode } from "react";
import { planLocked } from "@cliqsuite/db/powersync";
import { useRows, useSql } from "../lib/db";
import type { PairedDevice, SignedInStaff } from "../types";
import { useDevicePairing } from "../hooks/useDevicePairing";
import { useStaffSession } from "../hooks/useStaffSession";
import { useStoreLocationGate } from "../hooks/useStoreLocationGate";
import { STAFF_GEOFENCE_RADIUS_M } from "../lib/geofence";
import { resetDeviceAndReload } from "../lib/reset-device";
import { readSyncedBefore } from "./PosDbProvider";
import {
  LocationBlockedScreen,
  LocationPromptScreen,
  NotEnabledScreen,
  PairScreen,
  PinScreen,
  PlanEndedScreen,
  StaffPickerScreen,
  UnpairedScreen,
  WaitScreen,
} from "./Gate";

export interface StaffGateProps {
  /** Render prop: only invoked once the till is paired and a staff member is signed in. */
  children: (ctx: {
    device: PairedDevice;
    staff: SignedInStaff;
    unpairDevice: () => void;
    signOut: (options?: { clockOut?: boolean }) => void;
    clockedIn: boolean;
    clockingIn: boolean;
    clockIn: () => void;
    clockOut: () => void;
    /** Set by a blocked clockIn() (outside the geofence, or outside the
     * clock-in hours window) — surfaced in Settings' SHIFT section, since
     * that's the only place this can happen once already signed in. */
    shiftError: string;
  }) => ReactNode;
}

/** Full-screen gate: pair this till to a store, then have a staff member sign in with a PIN. */
export function StaffGate({ children }: StaffGateProps) {
  const pairing = useDevicePairing();
  const { device, unpair } = pairing;
  const session = useStaffSession(device?.storeId ?? null, device?.deviceId ?? null, device?.bypassGeofence ?? false);
  const locationGate = useStoreLocationGate(session.store, session.storeReady, device?.bypassGeofence ?? false);

  const business = useRows("businesses", "SELECT * FROM businesses WHERE id = $1", [device?.businessId ?? ""])?.rows[0];
  const waitingSales = useSql<{ n: number }>("SELECT COUNT(*) AS n FROM pending_sales")?.rows[0]?.n ?? 0;

  // Owner Portal unpaired this till while it ran: its data is already cleared.
  if (pairing.remoteUnpaired) {
    return <UnpairedScreen register={pairing.remoteUnpaired} waitingSales={waitingSales} onPairAgain={() => window.location.reload()} />;
  }

  if (!device) {
    return (
      <PairScreen
        error={pairing.error}
        pairing={pairing.pairing}
        onPair={(code) => void pairing.pair(code)}
        onClearError={pairing.clearError}
        onReset={resetDeviceAndReload}
        waitingSales={waitingSales}
      />
    );
  }

  const register = { storeName: device.storeName, registerName: device.registerName };

  // A plan that ended locks every register, signed in or not. It reopens by itself once paid.
  if (business && planLocked(business)) {
    return (
      <PlanEndedScreen
        register={register}
        businessName={business.name}
        waitingSales={waitingSales}
        onCheckAgain={() => window.location.reload()}
        onSignOut={session.signedIn ? () => session.signOut() : undefined}
      />
    );
  }

  if (business && !business.installed_modules.includes("pos")) {
    return <NotEnabledScreen register={register} onUnpair={unpair} />;
  }

  // A denied/insufficient location reading on a PIN sign-in or a shift resume blocks outright.
  if (session.locationBlocked) {
    return (
      <LocationBlockedScreen
        register={register}
        reason={session.locationBlocked}
        onRetry={session.retryLocationCheck}
        back={{ label: "Back", icon: "arrow_back", onClick: session.clearLocationBlocked }}
      />
    );
  }

  if (session.signedIn) {
    return (
      <>
        {children({
          device,
          staff: session.signedIn,
          unpairDevice: unpair,
          signOut: session.signOut,
          clockedIn: session.clockedIn,
          clockingIn: session.clockingIn,
          clockIn: session.clockIn,
          clockOut: session.clockOut,
          shiftError: session.error,
        })}
      </>
    );
  }

  // The staff picker and PIN only open once this device is confirmed at the store.
  const location = locationGate.status;
  if (location === "checking") return <WaitScreen register={register} text="Confirming this device's location…" />;
  if (location === "needs_gesture") return <LocationPromptScreen register={register} onAllow={locationGate.confirmLocation} onUnpair={unpair} />;
  if (location !== "not_required" && location !== "ok") {
    return (
      <LocationBlockedScreen
        register={register}
        reason={location}
        distanceM={locationGate.distanceM}
        radiusM={session.store?.geofence_radius_m ?? STAFF_GEOFENCE_RADIUS_M}
        onRetry={locationGate.recheck}
        back={{ label: "Unpair this register", icon: "link_off", onClick: unpair }}
      />
    );
  }

  const tile = (s: { id: string; name: string; role: string; avatar_color: string | null }) => ({ id: s.id, name: s.name, role: s.role, avatarColor: s.avatar_color });

  if (!session.picked) {
    // An empty roster only means "no staff" after a real sync; before that it means "still loading".
    const loading = !session.staffReady || (session.roster.length === 0 && !readSyncedBefore());
    return <StaffPickerScreen register={register} roster={loading ? null : session.roster.map(tile)} onPick={session.pick} onUnpair={unpair} />;
  }

  return (
    <PinScreen
      register={register}
      staff={tile(session.picked)}
      error={session.error}
      verifying={session.verifying}
      onBack={session.backToRoster}
      onPin={session.verifyPin}
    />
  );
}
